<template>
  <div class="mt-overview">
    <!-- Hero: search, quick actions and setup progress -->
    <section class="home-hero mt-hero">
      <div class="home-hero__main">
        <div class="home-hero__eyebrow">{{ $t('maintenance_overview.eyebrow', { name: getClient.name }) }}</div>
        <h1 class="home-hero__title">{{ $t('maintenance.title') }}</h1>
        <p class="home-hero__summary">{{ $t('maintenance_overview.intro') }}</p>

        <div
            class="home-search mt-search"
            :class="{ 'mt-search--open': searchOpen && query.trim() }"
            @keydown.down.prevent="moveResult(1)"
            @keydown.up.prevent="moveResult(-1)"
            @keydown.enter.prevent="chooseResult()"
            @keydown.esc="closeSearch"
        >
          <font-awesome-icon icon="search" class="home-search__icon"/>
          <input
              id="maintenance-search"
              ref="search"
              v-model="query"
              class="home-search__input"
              :placeholder="$t('maintenance_overview.search.placeholder')"
              :aria-label="$t('maintenance_overview.search.placeholder')"
              autocomplete="off"
              qid="maintenance-search"
              @focus="openSearch"
              @blur="closeSearchSoon"
          />
          <span class="home-kbd mt-search__kbd">{{ isMac ? '⌘K' : 'Ctrl+K' }}</span>
          <button type="button" class="mt-search__cancel" @mousedown.prevent="closeSearch">{{ $t('maintenance_overview.search.cancel') }}</button>

          <div v-if="searchOpen && query.trim()" class="home-search__results mt-search__results">
            <template v-for="group in searchGroups">
              <div :key="'g-' + group.key" class="home-search__group">{{ cardTitle(group) }}</div>
              <div
                  v-for="item in group.items"
                  :key="'r-' + item.key"
                  class="home-search__result mt-search__result"
                  :class="{ 'is-active': flatResults.indexOf(item) === activeResult }"
                  @mousedown.prevent="chooseResult(item)"
              >
                <span class="mt-search__icon" :class="'is-' + group.accent"><font-awesome-icon :icon="item.icon"/></span>
                <span class="mt-search__text">
                  <span class="home-search__name">{{ $t(item.label) }}</span>
                  <span class="home-search__meta">{{ $t('maintenance_overview.items.' + item.key) }}</span>
                </span>
                <span v-if="hasCount(item)" class="v2-tag" :class="countOf(item) ? 'v2-tag--grey' : 'v2-tag--amber'">{{ countLabel(item) }}</span>
              </div>
            </template>
            <div v-if="!flatResults.length" class="home-search__empty">{{ $t('maintenance_overview.search.no_match', { query: query.trim() }) }}</div>
          </div>
        </div>

        <div v-if="quickActions.length" class="home-hero__chips mt-hero__chips">
          <button
              v-for="action in quickActions"
              :key="action.key"
              type="button"
              class="home-chip"
              :qid="'maintenance-quick-' + action.key"
              @click="add(action.item)"
          >
            <font-awesome-icon icon="plus"/>{{ $t('maintenance_overview.actions.' + action.key) }}
          </button>
        </div>
      </div>

      <div v-if="progress.total" class="home-week mt-progress">
        <div class="home-week__top">
          <svg
              class="home-ring"
              width="96"
              height="96"
              viewBox="0 0 96 96"
              aria-hidden="true"
          >
            <circle
                class="home-ring__track"
                cx="48"
                cy="48"
                r="42"
            />
            <circle
                v-if="progress.done"
                class="home-ring__value"
                cx="48"
                cy="48"
                r="42"
                :stroke-dasharray="ringDash(progress.done, progress.total, 42)"
            />
          </svg>
          <b class="home-week__count">{{ progress.done }}/{{ progress.total }}</b>
          <div>
            <div class="home-week__title">{{ $t('maintenance_overview.progress.title') }}</div>
            <div class="home-week__text">{{ progressText }}</div>
          </div>
        </div>
      </div>
    </section>

    <div class="mt-body">
      <!-- Area cards -->
      <div class="mt-areas">
        <section
            v-for="card in cards"
            :key="card.key"
            class="v2-card mt-area"
            :class="{ 'is-open': isCardOpen(card) }"
        >
          <button
              type="button"
              class="mt-area__head"
              :aria-expanded="isCardOpen(card) ? 'true' : 'false'"
              :qid="'maintenance-area-' + card.key"
              @click="toggleCard(card)"
          >
            <span class="mt-area__icon" :class="'is-' + card.accent"><font-awesome-icon :icon="card.icon"/></span>
            <span class="mt-area__titles">
              <b>{{ cardTitle(card) }}</b>
              <span class="mt-area__desc">{{ $t('maintenance_overview.areas.' + card.key) }}</span>
              <span class="mt-area__summary">{{ card.items.map(item => $t(item.label)).join(', ') }}</span>
            </span>
            <span v-if="cardProgress(card).total" class="mt-area__xy" :class="cardProgress(card).done < cardProgress(card).total ? 'is-amber' : 'is-' + card.accent">
              {{ cardProgress(card).done }}/{{ cardProgress(card).total }}
            </span>
            <font-awesome-icon icon="angle-right" class="mt-area__chevron"/>
          </button>

          <div class="mt-rows">
            <div
                v-for="item in card.items"
                :key="item.key"
                class="mt-row"
                :class="{ 'is-empty': hasCount(item) && countOf(item) === 0 }"
            >
              <router-link :to="item.to" class="mt-row__link" :qid="'maintenance-row-' + item.key">
                <font-awesome-icon :icon="item.icon" class="mt-row__icon" fixed-width/>
                <span class="mt-row__name">{{ $t(item.label) }}</span>
                <span v-if="loading && item.count" class="mt-skeleton"></span>
                <span v-else-if="hasCount(item)" class="v2-tag" :class="countOf(item) ? 'v2-tag--grey' : 'v2-tag--amber'">{{ countLabel(item) }}</span>
              </router-link>
              <button
                  v-if="item.canCreate"
                  type="button"
                  class="mt-row__add"
                  :title="$t('maintenance_overview.add', { name: $t(item.label) })"
                  :aria-label="$t('maintenance_overview.add', { name: $t(item.label) })"
                  :qid="'maintenance-add-' + item.key"
                  @click="add(item)"
              >
                <font-awesome-icon icon="plus"/>
              </button>
              <font-awesome-icon icon="angle-right" class="mt-row__chevron"/>
            </div>
          </div>
        </section>
      </div>

      <!-- Needs attention -->
      <section class="v2-card mt-attention">
        <div class="v2-card__head">
          <h3>{{ $t('maintenance_overview.attention.title') }}</h3>
          <span v-if="attention.length" class="mt-badge">{{ attention.length }}</span>
        </div>
        <div v-for="item in attention" :key="item.key" class="v2-att mt-att">
          <span class="v2-att__icon" :class="'v2-att__icon--' + item.color"><font-awesome-icon :icon="item.icon"/></span>
          <span class="v2-att__text">
            {{ item.title }}
            <small class="mt-att__sub">{{ item.text }}</small>
          </span>
          <a
              v-if="item.actionLabel"
              href="#"
              class="v2-link"
              :qid="'maintenance-attention-' + item.key"
              @click.prevent="openAttention(item)"
          >{{ item.actionLabel }}</a>
        </div>
        <div v-if="!loading && !attention.length" class="home-empty home-empty--small">{{ $t('maintenance_overview.attention.empty') }}</div>
      </section>

      <!-- Recent changes: read from the history log -->
      <section class="v2-card mt-recent">
        <div class="v2-card__head"><h3>{{ $t('maintenance_overview.recent.title') }}</h3></div>
        <div class="v2-card__body">
          <ActivityFeed v-if="recent.length || !recentLoading"
                        compact
                        :prepared="recentEntries"
                        :empty-text="$t('maintenance_overview.recent.empty')"
          />
        </div>
        <div v-if="recent.length < recentTotal" class="mt-recent__more">
          <button
              type="button"
              class="v2-link mt-link-button"
              qid="maintenance-recent-more"
              :disabled="recentLoading"
              @click="loadRecent(true)"
          >
            {{ $t('maintenance_overview.recent.show_more') }}
          </button>
        </div>
      </section>
    </div>

    <UserInviteModal
        v-if="isGranted(permissions.USER_CREATE)"
        ref="invite"
        buttonType="none"
        @reloadUserList="loadOverview"
    ></UserInviteModal>
    <PartitionFormModal
        v-if="isGranted(permissions.PARTITION_CREATE)"
        ref="partition"
        buttonType="none"
        @saved="loadOverview"
    ></PartitionFormModal>
  </div>
</template>

<script>
import piincHttp from '@/util/configureAxios.js';
import permissionMap from '@/util/ACL/permissionMap.js';
import UserInviteModal from '@/components/user/UserInviteModal.vue';
import PartitionFormModal from '@/components/partition/PartitionFormModal.vue';
import { maintenanceCards } from '@/util/maintenanceSettings.js';
import ActivityFeed from '@/components/activity/feed/ActivityFeed.vue';
import { maintenanceEntry } from '@/components/activity/feed/summaryEntries.js';


const RECENT_PAGE = 5;
const MAX_ATTENTION = 5;
const QUICK_ACTIONS = [
  { key: 'invite_user', item: 'users' },
  { key: 'data_object', item: 'data_objects' },
  { key: 'measure', item: 'measures' },
  { key: 'framework', item: 'frameworks' },
];

export default {
  name: 'MaintenanceOverview',
  components: {
    UserInviteModal,
    PartitionFormModal,
    ActivityFeed
  },
  data: function() {
    return {
      counts: {},
      unusedDataObjects: 0,
      loading: true,
      recent: [],
      recentTotal: 0,
      recentPage: 0,
      recentLoading: true,
      query: '',
      searchOpen: false,
      activeResult: 0,
      toggledCards: {},
      isMac: typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform)
    }
  },
  computed: {
    getClient() {
      return this.$store.state.client
    },
    recentEntries() {
      return this.recent.map(row => maintenanceEntry(this, row));
    },
    cards() {
      return maintenanceCards(this.getClient || {}, permission => this.isGranted(permissionMap[permission]));
    },
    items() {
      return this.cards.reduce((items, card) => items.concat(card.items), []);
    },
    // Settings that count towards the setup progress: the ones with a count the user may see
    countedItems() {
      return this.items.filter(item => this.hasCount(item));
    },
    missingItems() {
      return this.countedItems.filter(item => this.countOf(item) === 0);
    },
    progress() {
      return {
        done: this.countedItems.length - this.missingItems.length,
        total: this.countedItems.length
      };
    },
    progressText() {
      const names = this.missingItems.map(item => this.$t(item.label));
      if (!names.length) {
        return this.$t('maintenance_overview.progress.all_done');
      }
      if (names.length > 2) {
        return this.$t('maintenance_overview.progress.left_more', { names: names.slice(0, 2).join(', '), count: names.length - 2 });
      }
      return this.$t('maintenance_overview.progress.left', { names: names.join(', ') });
    },
    quickActions() {
      return QUICK_ACTIONS
        .map(action => Object.assign({}, action, { item: this.items.find(item => item.key === action.item) }))
        .filter(action => action.item && action.item.canCreate);
    },
    attention() {
      let list = this.missingItems.slice(0, 2).map(item => ({
        key: 'missing-' + item.key,
        color: 'amber',
        icon: item.icon,
        title: this.$t('maintenance_overview.attention.not_set_up', { name: this.$t(item.label) }),
        text: this.$t('maintenance_overview.attention.not_set_up_text'),
        actionLabel: this.$t('maintenance_overview.attention.set_up'),
        item: item,
        create: item.canCreate
      }));
      if (this.missingItems.length > 2) {
        const more = this.missingItems.length - 2;
        list.push({
          key: 'missing-more',
          color: 'amber',
          icon: 'exclamation-triangle',
          title: this.$tc('maintenance_overview.attention.more_not_set_up', more, { count: more }),
          text: this.missingItems.slice(2).map(item => this.$t(item.label)).join(', ')
        });
      }
      const dataObjects = this.items.find(item => item.key === 'data_objects');
      if (this.unusedDataObjects && dataObjects && this.getClient.gdprModule) {
        list.push({
          key: 'unused-data-objects',
          color: 'blue',
          icon: 'clipboard',
          title: this.$tc('maintenance_overview.attention.unused_data_objects', this.unusedDataObjects, { count: this.unusedDataObjects }),
          text: this.$t('maintenance_overview.attention.unused_data_objects_text'),
          actionLabel: this.$t('maintenance_overview.attention.view'),
          item: dataObjects,
          create: false
        });
      }
      const order = { red: 0, amber: 1, blue: 2 };
      return list.sort((a, b) => order[a.color] - order[b.color]).slice(0, MAX_ATTENTION);
    },
    searchGroups() {
      const query = this.query.trim().toLowerCase();
      return this.cards
        .map(card => Object.assign({}, card, {
          items: card.items.filter(item => (this.$t(item.label) + ' ' + this.$t('maintenance_overview.items.' + item.key)).toLowerCase().indexOf(query) !== -1)
        }))
        .filter(card => card.items.length);
    },
    flatResults() {
      return this.searchGroups.reduce((items, group) => items.concat(group.items), []);
    }
  },
  watch: {
    getClient: function(newValue, oldValue) {
      if (newValue && (!oldValue || newValue.slug !== oldValue.slug)) {
        this.toggledCards = {};
        this.load();
      }
    },
    query: function() {
      this.activeResult = 0;
    }
  },
  methods: {
    load() {
      this.loadOverview();
      this.loadRecent(false);
    },
    loadOverview() {
      let self = this;
      this.loading = true;
      piincHttp.get('maintenance/overview', { params: {
        client: this.getClient.slug
      }}).then(function(response) {
        self.counts = response.data.counts || {};
        self.unusedDataObjects = (response.data.attention || {}).unused_data_objects || 0;
        self.loading = false;
      }, function() {
        self.loading = false;
      });
    },
    loadRecent(more) {
      let self = this;
      const page = more ? this.recentPage + 1 : 1;
      this.recentLoading = true;
      piincHttp.get('maintenance/recent-changes', { params: {
        client: this.getClient.slug,
        page: page,
        per_page: RECENT_PAGE
      }}).then(function(response) {
        self.recent = more ? self.recent.concat(response.data.items) : response.data.items;
        self.recentTotal = response.data.total_count;
        self.recentPage = page;
        self.recentLoading = false;
      }, function() {
        self.recentLoading = false;
      });
    },
    hasCount(item) {
      return !!item.count && this.counts[item.count] !== null && this.counts[item.count] !== undefined;
    },
    countOf(item) {
      return this.counts[item.count];
    },
    countLabel(item) {
      return this.countOf(item) ? this.countOf(item) : this.$t('maintenance_overview.not_set_up');
    },
    cardTitle(card) {
      return this.$t('maintenance_overview.area_titles.' + card.key);
    },
    cardProgress(card) {
      const counted = card.items.filter(item => this.hasCount(item));
      return {
        done: counted.filter(item => this.countOf(item) > 0).length,
        total: counted.length
      };
    },
    // On phones the area cards fold; General settings and cards with something missing start open
    isCardOpen(card) {
      if (this.toggledCards[card.key] !== undefined) {
        return this.toggledCards[card.key];
      }
      return card.key === 'general' || this.cardProgress(card).done < this.cardProgress(card).total;
    },
    toggleCard(card) {
      this.$set(this.toggledCards, card.key, !this.isCardOpen(card));
    },
    add(item) {
      if (item.invite) {
        this.$refs.invite.showInviteModal();
      } else if (item.partitionModal) {
        this.$refs.partition.showCreatePartitionModal();
      } else if (item.create) {
        this.$router.push(item.create);
      }
    },
    openAttention(entry) {
      if (entry.create) {
        this.add(entry.item);
      } else {
        this.$router.push(entry.item.to);
      }
    },
    ringDash(done, total, radius) {
      const circumference = 2 * Math.PI * radius;
      return (circumference * done / total) + ' ' + circumference;
    },
    openSearch() {
      this.searchOpen = true;
    },
    closeSearch() {
      this.searchOpen = false;
      this.query = '';
      this.$refs.search.blur();
    },
    closeSearchSoon() {
      setTimeout(() => {
        this.searchOpen = false;
      }, 150);
    },
    moveResult(step) {
      if (!this.flatResults.length) {
        return;
      }
      this.activeResult = (this.activeResult + step + this.flatResults.length) % this.flatResults.length;
    },
    chooseResult(item) {
      const chosen = item || this.flatResults[this.activeResult];
      if (chosen) {
        this.$router.push(chosen.to);
      }
    },
    // ⌘K / Ctrl+K jumps to the search
    onKeydown(event) {
      if ((event.metaKey || event.ctrlKey) && (event.key === 'k' || event.key === 'K')) {
        event.preventDefault();
        this.$refs.search.focus();
      }
    }
  },
  created() {
    if (this.getClient && this.getClient.slug) {
      this.load();
    }
  },
  mounted() {
    document.addEventListener('keydown', this.onKeydown);
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.onKeydown);
  }
}
</script>
